import { useFpCampaignCartService } from '@cart-services/hooks/useFpCampaignCartService'
import { useFpCampaignProductCatalogProducts } from '@cart-services/hooks/useFpCampaignProductCatalogProducts'
import { type CartItem } from '@cart-services/types'
import { DialogSelectAddonProductVariationContent, type DialogSelectAddonProductVariationContentProps } from '@cart-ui/DialogSelectAddonProductVariationContent/DialogSelectAddonProductVariationContent'
import { type ProjectCode } from '@constants'
import { useDialog } from '@dni-hooks/useDialog'
import { Box } from '@pets-ui/Box/Box'
import { CircularProgress } from '@pets-ui/CircularProgress/CircularProgress'
import { IconButton } from '@pets-ui/IconButton/IconButton'
import { CloseLarge } from '@pets-ui/icons'
import { ResponsiveDialog } from '@pets-ui/ResponsiveDialog/ResponsiveDialog'
import { colors } from '@pets-ui/tokens/colors.stylex'
import * as stylex from '@stylexjs/stylex'
import { useMemo } from 'react'
import { useContextCampaignCart } from '../CartProvider/CampaignCartProvider'

export const DIALOG_SELECT_PRODUCT_VARIATION = 'DIALOG_SELECT_PRODUCT_VARIATION'

const styles = stylex.create({
  closeButton: {
    position: 'absolute',
    right: 16,
    top: 14,
  },
})

export interface DialogSelectProductVariationProps {
  parentProductId: number
  productProjectCode: ProjectCode
  currency?: string
  currentCartItem?: CartItem
}

export const DialogSelectProductVariation = () => {
  const { parentProductId, productProjectCode, currency, open, currentCartItem, closeDialog } = useDialog<DialogSelectProductVariationProps>(DIALOG_SELECT_PRODUCT_VARIATION)
  const { campaignId, campaignCode } = useContextCampaignCart()
  const { upsertCartItem, replaceCartItem } = useFpCampaignCartService(campaignId)
  const { productCatalogDto, isLoading: isProductFetching } = useFpCampaignProductCatalogProducts(productProjectCode, campaignCode)

  const handleSubmit: DialogSelectAddonProductVariationContentProps['onSubmit'] = (selectedVariation, quantity) => {
    if (selectedVariation) {
      /**
       * 如果 currentCartItem 存在，且 currentCartItem.cartItemId 與 selectedVariation.variation_id 不同表示是替換規格，使用 replaceCartItem 替換
       */
      if (currentCartItem && currentCartItem.cartItemId !== selectedVariation.variation_id) {
        replaceCartItem(currentCartItem.cartItemId, {
          ...selectedVariation,
          cartItemId: selectedVariation.variation_id,
          quantity,
        })
      }
      else {
        upsertCartItem({
          ...selectedVariation,
          cartItemId: selectedVariation.variation_id,
          quantity,
        })
      }
    }
    closeDialog()
  }

  const allProductDto = useMemo(() => {
    const products = productCatalogDto?.products.flatMap(product => product.all_product)
    return products?.find(product => product.id === parentProductId)
  }, [productCatalogDto, parentProductId])

  const selectedVariation = useMemo(() => allProductDto?.variations?.find(variation => variation.variation_id === currentCartItem?.cartItemId),
    [allProductDto?.variations, currentCartItem?.cartItemId])

  return (
    <ResponsiveDialog
      open={open}
      fullWidth
      maxWidth={480}
      onClose={closeDialog}
    >
      <IconButton rootStyle={styles.closeButton} onClick={closeDialog}>
        <CloseLarge color={colors.grey900} />
      </IconButton>
      {
        isProductFetching
          ? (
            <Box display="flex" alignItems="center" justifyContent="center" height="300px" width="100%">
              <CircularProgress fontSize={28} />
            </Box>
          )
          : (
            <DialogSelectAddonProductVariationContent
              currency={currency}
              onSubmit={handleSubmit}
              quantity={currentCartItem?.quantity}
              variationsGroupByFirstLevelButton={allProductDto?.variationsGroupByFirstLevelButton}
              selectedVariation={selectedVariation}
            />
          )
      }
    </ResponsiveDialog>
  )
}
